import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Phone, MessageCircle, Globe, ChevronLeft, 
  Filter, SortAsc, Eye, Star, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { getCityBySlug, getBusinesses, getCategories, getBusinessesCount, trackBusinessAction } from '@/lib/api';
import DonenVitrin from '@/components/DonenVitrin';

export default function CityPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  
  const [city, setCity] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [anaVitrinItems, setAnaVitrinItems] = useState([]);
  const [donenVitrinItems, setDonenVitrinItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch city
      const cityResponse = await getCityBySlug(citySlug);
      setCity(cityResponse.data);
      
      // Fetch categories
      const catResponse = await getCategories(true);
      setCategories(catResponse.data);
      
      // Fetch showcase items
      const anaResponse = await getBusinesses({ 
        city_slug: citySlug, 
        ana_vitrin: true, 
        status: 'active',
        sort_by: 'ana_vitrin_order',
        sort_order: 'asc'
      });
      setAnaVitrinItems(anaResponse.data);
      
      const donenResponse = await getBusinesses({ 
        city_slug: citySlug, 
        donen_vitrin: true, 
        status: 'active',
        sort_by: 'donen_vitrin_order',
        sort_order: 'asc'
      });
      setDonenVitrinItems(donenResponse.data);
      
      // Fetch regular listings
      const params = {
        city_slug: citySlug,
        status: 'active',
        sort_by: sortBy,
        sort_order: sortBy === 'views' ? 'desc' : 'desc',
        limit: pageSize,
        skip: (currentPage - 1) * pageSize
      };
      
      if (selectedCategory !== 'all') {
        params.category_id = selectedCategory;
      }
      
      const bizResponse = await getBusinesses(params);
      setBusinesses(bizResponse.data);
      
      // Get total count
      const countParams = { city_slug: citySlug, status: 'active' };
      if (selectedCategory !== 'all') {
        countParams.category_id = selectedCategory;
      }
      const countResponse = await getBusinessesCount(countParams);
      setTotalCount(countResponse.data.count);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 404) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [citySlug, selectedCategory, sortBy, pageSize, currentPage, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBusinessClick = (business) => {
    navigate(`/${citySlug}/${business.slug}-${business.id}`);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || '';
  };

  if (loading && !city) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background page-enter">
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <Link to="/" className="font-heading text-xl font-semibold">
                <span className="text-foreground">Firma</span>
                <span className="text-primary">Burada</span>
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="font-heading font-semibold">{city?.name}</span>
          </div>
        </div>
      </header>

      {/* Ana Vitrin - Featured Showcase */}
      {anaVitrinItems.length > 0 && (
        <section className="py-12 px-6 md:px-12">
          <h2 className="font-heading text-2xl font-semibold mb-6 flex items-center gap-2">
            <Star className="w-6 h-6 text-primary" />
            Ana Vitrin
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {anaVitrinItems.map((business, index) => (
              <motion.div
                key={business.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div
                  data-testid={`ana-vitrin-item-${business.id}`}
                  onClick={() => handleBusinessClick(business)}
                  className="relative h-64 rounded-xl overflow-hidden cursor-pointer group amber-glow"
                >
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                    style={{
                      backgroundImage: business.cover_image_url 
                        ? `url(${business.cover_image_url})`
                        : 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--secondary)) 100%)'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <Badge className="mb-2 bg-primary/20 text-primary border-primary/30">
                      {getCategoryName(business.category_id)}
                    </Badge>
                    <h3 className="font-heading text-xl font-semibold text-white mb-1">
                      {business.name}
                    </h3>
                    {business.district && (
                      <p className="text-white/70 text-sm flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {business.district}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Dönen Vitrin - Rotating Showcase */}
      {donenVitrinItems.length > 0 && (
        <section className="py-6 overflow-hidden">
          <DonenVitrin 
            items={donenVitrinItems} 
            onItemClick={handleBusinessClick}
            getCategoryName={getCategoryName}
          />
        </section>
      )}

      {/* Filters */}
      <section className="py-8 px-6 md:px-12 border-t border-white/10">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48 bg-card border-white/10" data-testid="category-filter">
                <SelectValue placeholder="Kategori seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <SortAsc className="w-4 h-4 text-muted-foreground" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 bg-card border-white/10" data-testid="sort-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">En Yeni</SelectItem>
                <SelectItem value="views">En Çok Görüntülenen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-muted-foreground text-sm">Göster:</span>
            <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(parseInt(v))}>
              <SelectTrigger className="w-24 bg-card border-white/10" data-testid="pagesize-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="500">500</SelectItem>
                <SelectItem value="1000">1000</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <p className="text-muted-foreground text-sm mt-4">
          Toplam {totalCount} işletme bulundu
        </p>
      </section>

      {/* Business Listings */}
      <section className="py-8 px-6 md:px-12 pb-24">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-card rounded-xl animate-pulse" />
            ))}
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">Henüz işletme bulunmuyor</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {businesses.map((business, index) => (
              <motion.div
                key={business.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <div
                  data-testid={`business-card-${business.id}`}
                  className="bg-card border border-white/10 rounded-xl p-4 hover:border-primary/30 transition-colors cursor-pointer group"
                  onClick={() => handleBusinessClick(business)}
                >
                  <div className="flex gap-4">
                    {/* Logo/Image */}
                    <div className="w-20 h-20 rounded-lg bg-secondary flex-shrink-0 overflow-hidden">
                      {business.logo_url ? (
                        <img 
                          src={business.logo_url} 
                          alt={business.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl font-heading font-bold text-primary">
                          {business.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-heading font-semibold text-lg truncate group-hover:text-primary transition-colors">
                          {business.name}
                        </h3>
                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                          {getCategoryName(business.category_id)}
                        </Badge>
                      </div>
                      
                      {business.district && (
                        <p className="text-muted-foreground text-sm flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" /> {business.district}
                        </p>
                      )}
                      
                      {business.description && (
                        <p className="text-muted-foreground text-sm mt-2 line-clamp-2">
                          {business.description}
                        </p>
                      )}
                      
                      {/* Stats */}
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {business.views || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                    {business.phone && (
                      <Button 
                        size="sm" 
                        variant="secondary"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          trackBusinessAction(business.id, 'call');
                          window.location.href = `tel:${business.phone}`;
                        }}
                      >
                        <Phone className="w-4 h-4 mr-1" /> Ara
                      </Button>
                    )}
                    {business.whatsapp && (
                      <Button 
                        size="sm" 
                        variant="secondary"
                        className="flex-1 bg-green-500/10 hover:bg-green-500/20 text-green-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          trackBusinessAction(business.id, 'whatsapp');
                          window.open(`https://wa.me/${business.whatsapp}`, '_blank');
                        }}
                      >
                        <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                      </Button>
                    )}
                    {business.website && (
                      <Button 
                        size="sm" 
                        variant="secondary"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          trackBusinessAction(business.id, 'website');
                          window.open(business.website, '_blank');
                        }}
                      >
                        <Globe className="w-4 h-4 mr-1" /> Web
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {pageSize === 50 && totalCount > 50 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Önceki
            </Button>
            <span className="flex items-center px-4 text-muted-foreground">
              Sayfa {currentPage} / {Math.ceil(totalCount / pageSize)}
            </span>
            <Button
              variant="outline"
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Sonraki
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
